import React, { useEffect, useState } from 'react';
import { digitsFaToEn } from '@persian-tools/persian-tools';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CardActionArea from '@mui/material/CardActionArea';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

import moment from 'moment/moment';
import NewDialog from 'components/NewDialog';
import NewDialogTitle from 'components/NewDialogTitle';
import NewDialogContent from 'components/NewDialogContent';
import NewDialogActions from 'components/NewDialogActions';
import { FormattedMessage } from 'react-intl';
import FieldClearButton from '../../FieldClearButton';

function helper(date) {
  const now = date ? date.slice(0, 5) : digitsFaToEn(moment().format('HH:mm'));
  const [h = '00', m = '00'] = now.split(':');
  return { h, m, now };
}

export default function Date(props) {
  const { schema, formData, onChange, disabled, readonly, rawErrors, required, formContext } = props;

  const closeOnSelect = true;
  const [open, setOpen] = useState(false);

  const { h, m, now } = helper(formData);
  const [hour, setHour] = useState(h);
  const [minute, setMinute] = useState(m);
  const [selectedValue, setSelectedValue] = useState(formData ? now : '');

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  const goToNow = () => {
    const { h, m, now } = helper();
    setHour(h);
    setMinute(m);
    setSelectedValue(now);
    closeOnSelect && onClose();
  };

  const onChangeHour = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && (value === '' || (Number(value) >= 0 && Number(value) <= 23))) {
      setHour(value);
    }
  };

  const onChangeMinute = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && (value === '' || (Number(value) >= 0 && Number(value) <= 59))) {
      setMinute(value);
    }
  };

  const onSelect = ({ closeOnSelect = true }) => {
    setSelectedValue(`${hour}:${minute}`);
    onChange(`${hour}:${minute}`);
    closeOnSelect && onClose();
  };

  const onPrevHour = () => {
    if (+hour === 0) {
      setHour(23);
      return;
    }
    return setHour((prev) => +prev - 1);
  };

  const onNextHour = () => {
    if (+hour === 23) {
      setHour(0);
      return;
    }
    return setHour((prev) => +prev + 1);
  };

  const onPrevMinute = () => {
    if (+minute === 0) {
      setMinute(59);
      return onPrevHour();
    }
    return setMinute((prev) => +prev - 1);
  };

  const onNextMinute = () => {
    if (+minute === 59) {
      setMinute(0);
      return onNextHour();
    }
    return setMinute((prev) => +prev + 1);
  };

  const renderClockFace = () => {
    const hourDegrees = (+hour % 12) * 30 + (+minute / 60) * 30 + 90; // 360° / 12 hours = 30° per hour
    const minuteDegrees = +minute * 6 + 90; // 360° / 60 minutes = 6° per minute

    return (
      <Box
        sx={{
          width: '200px',
          height: '200px',
          display: 'flex',
          borderRadius: '50%',
          border: '4px solid',
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: 'action.selected',
          bgcolor: 'Background.paper',
          boxShadow: (theme) => theme.shadows[10],
        }}
      >
        {/* عقربه ساعت */}
        <Box
          sx={{
            zIndex: 1,
            width: '60px',
            height: '4px',
            position: 'absolute',
            translate: '-30px 0',
            bgcolor: 'primary.main',
            transformOrigin: '100% 50%',
            transform: `rotate(${hourDegrees}deg)`,
          }}
        />
        {/* عقربه دقیقه */}
        <Box
          sx={{
            zIndex: 1,
            width: '80px',
            height: '2px',
            position: 'absolute',
            translate: '-40px 0',
            bgcolor: 'primary.dark',
            transformOrigin: '100% 50%',
            transform: `rotate(${minuteDegrees}deg)`,
          }}
        />
        {/* مرکز ساعت */}
        <Box
          sx={{
            zIndex: 1,
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            position: 'absolute',
            bgcolor: 'primary.darker',
          }}
        />
        <Box
          sx={{
            top: '20%',
            position: 'absolute',
          }}
        >
          <Typography color="textDisabled" variant="subtitle1" fontSize={10}>
            Satrapsazeh
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          position: 'relative',
          borderColor: rawErrors && rawErrors.length > 0 ? 'error' : 'action.disabledBackground',
        }}
      >
        <CardActionArea
          onClick={onOpen}
          disabled={disabled || readonly}
          focusRipple={rawErrors && rawErrors.length > 0}
        >
          <Stack px={1} pr={formContext?.clearable ? 5 : 1} height={56} columnGap={0.5} direction="row" alignItems="center" alignContent="center">
            <Typography flexGrow={1} fontSize={12} align="right" variant="body1" color="textSecondary">
              <FormattedMessage id={schema.title} />
            </Typography>
            {selectedValue && (
              <Chip sx={{ width: 40, fontSize: 10 }} size="small" disabled={disabled} label={h > 12 ? 'ب.ظ' : 'ق.ظ'} />
            )}
            <Typography align="left" variant="subtitle2" sx={{ mx: 1, minWidth: 40 }}>
              {selectedValue || 'انتخاب'}
            </Typography>
            <AccessTimeRoundedIcon color="disabled" fontSize="small" />
          </Stack>
        </CardActionArea>
        {formContext?.clearable && (
          <FieldClearButton
            disabled={disabled || readonly}
            onClear={() => {
              setSelectedValue('');
              onChange(undefined);
            }}
          />
        )}
      </Card>
      <NewDialog label="ساعت" open={open} onClose={onClose}>
        <NewDialogTitle onClose={onClose} title={<FormattedMessage id={schema.title} />} />
        <NewDialogContent isPopup>
          <Stack p={1} flex={1} rowGap={1} alignItems="center" bgcolor="background.paper">
            <Box py={4}>{renderClockFace()}</Box>
            <Stack columnGap={1} alignItems="center" justifyContent="center" direction="row-reverse">
              <Stack>
                <Button size="small" onClick={onNextHour}>
                  <ArrowDropUpRoundedIcon />
                </Button>
                <Button size="small" onClick={onPrevHour}>
                  <ArrowDropDownRoundedIcon />
                </Button>
              </Stack>
              <TextField
                sx={{ width: 80 }}
                type="number"
                label="ساعت"
                value={+hour}
                onChange={onChangeHour}
                inputProps={{
                  min: 0,
                  max: 23,
                  step: 1,
                  style: {
                    fontSize: 18,
                    fontWeight: 'bold',
                    textAlign: 'center',
                  },
                }}
              />
              :
              <TextField
                sx={{ width: 80 }}
                type="number"
                label="دقیقه"
                value={+minute}
                onChange={onChangeMinute}
                inputProps={{
                  min: 0,
                  max: 59,
                  step: 1,
                  style: {
                    fontSize: 18,
                    fontWeight: 'bold',
                    textAlign: 'center',
                  },
                }}
              />
              <Stack>
                <Button size="small" onClick={onNextMinute}>
                  <ArrowDropUpRoundedIcon />
                </Button>
                <Button size="small" onClick={onPrevMinute}>
                  <ArrowDropDownRoundedIcon />
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </NewDialogContent>
        <NewDialogActions>
          <Stack width="100%" alignItems="center" direction="row-reverse" justifyContent="space-between">
            <Button size="medium" color="primary" variant="contained" onClick={onSelect}>
              انتخاب
            </Button>
            {selectedValue !== digitsFaToEn(moment().format('HH:mm')) && (
              <Button
                size="medium"
                color="primary"
                variant="outlined"
                onClick={goToNow}
                endIcon={<AccessTimeRoundedIcon />}
              >
                همین الان
              </Button>
            )}
          </Stack>
        </NewDialogActions>
      </NewDialog>
    </>
  );
}
